import { create } from 'zustand';

export type PlayerColor = 'red' | 'blue' | 'green' | 'yellow' | 'purple' | 'orange';

export interface Player {
  id: string;
  name: string;
  color: PlayerColor;
  position: number;
  isInPrison: boolean;
  sips: number;
}

export type SpaceType = 'normal' | 'prison' | 'trivia' | 'game' | 'fame' | 'start';

export interface BoardSpace {
  id: number;
  type: SpaceType;
  label: string;
  color?: string;
}

export type GamePhase = 'setup' | 'playing' | 'dice-roll' | 'prison-roll' | 'trivia' | 'game' | 'penalty-roll' | 'finished';

export interface TriviaQuestion {
  id: number;
  question: string;
  options: string[];
  correctAnswer: number;
}

export interface GameState {
  // Game setup
  phase: GamePhase;
  currentPlayerIndex: number;
  players: Player[];
  boardSpaces: BoardSpace[];
  
  // Dice
  diceValue: number | null;
  isRolling: boolean;
  canRoll: boolean;
  
  // Trivia
  currentTrivia: TriviaQuestion | null;
  triviaAnswered: boolean;
  
  // Mini-game
  showMiniGame: boolean;
  miniGameType: 'truth_or_dare' | 'charades' | null;
  charadesTimeLeft: number;
  
  // Penalty roll (wrong trivia / failed T&D / failed charades)
  penaltyRolling: boolean;
  pendingPenaltySpaces: number;
  
  // Money confetti
  showMoneyConfetti: boolean;

  // Pending win after Fame & Fortune video
  pendingWin: boolean;

  // Pending prison after Back to Prison video
  pendingPrison: boolean;

  // Video playback for destination cards
  playingVideo: 'the-streets' | 'hustle-hard' | 'side-quest' | 'grind-mode' | 'close-call' | 'stackin-paper' | 'the-come-up' | 'loyal-friends' | 'betrayal' | 'breaking-free' | 'the-deal' | 'close-call-2' | 'almost-there' | 'final-stretch' | 'one-more-step' | 'victory-lap' | 'fame-fortune' | 'back-to-prison' | null;

  // Charades rolling phase
  charadesRollingPhase: boolean;
  charadesActorId: string | null;
  charadesGuesserId: string | null;
  charadesActorRolled: boolean;
  charadesGuesserRolled: boolean;
  charadesWaitingForVideo: boolean;
  charadesCurrentRollerId: string | null;
  charadesPrisonPlayerId: string | null;

  // Actions
  addPlayer: (name: string, color: PlayerColor) => void;
  removePlayer: (id: string) => void;
  startGame: () => void;
  rollDice: () => void;
  finishRolling: (value: number) => void;
  moveCurrentPlayer: (spaces: number) => void;
  movePlayer: (playerId: string, spaces: number) => void;
  sendToPrison: (playerId: string) => void;
  freeFromPrison: (playerId: string) => void;
  nextTurn: () => void;
  addSips: (playerId: string, amount: number) => void;
  
  // Trivia actions
  showTriviaQuestion: () => void;
  answerTrivia: (correct: boolean) => void;
  closeTrivia: () => void;
  
  // Mini-game actions
  startMiniGame: (type: 'truth_or_dare' | 'charades') => void;
  endMiniGame: (result: boolean | { type: string; actorId?: string; guesserId?: string }) => void;
  setCharadesTime: (time: number) => void;
  
  // Penalty actions
  penaltyRollDice: () => void;
  finishPenaltyRolling: (value: number) => void;
  
  // Album triggers
  albumTriggerDice: () => void;
  
  // Money confetti
  triggerMoneyConfetti: () => void;
  hideMoneyConfetti: () => void;

  // Pending win
  setPendingWin: (val: boolean) => void;

  // Video playback
  setPlayingVideo: (video: 'the-streets' | 'hustle-hard' | 'side-quest' | 'grind-mode' | 'close-call' | 'stackin-paper' | 'the-come-up' | 'loyal-friends' | 'betrayal' | 'breaking-free' | 'the-deal' | 'close-call-2' | 'almost-there' | 'final-stretch' | 'one-more-step' | 'victory-lap' | 'fame-fortune' | 'back-to-prison' | null) => void;

  setPendingPrison: (val: boolean) => void;

  // Charades rolling actions
  startCharadesRolling: (actorId: string, guesserId: string) => void;
  finishCharadesRolling: (playerId: string, value: number) => void;
  charadesContinueAfterVideo: () => void;
  charadesMarkRollerDone: () => void;

  // Sidebar state
  sidebarsOpen: boolean;
  roundTurnsCompleted: number;
}

const BOARD_SPACES: BoardSpace[] = [
  { id: 0, type: 'start', label: 'PRISON', color: '#dc2626' },
  { id: 1, type: 'normal', label: 'The Streets' },
  { id: 2, type: 'trivia', label: 'TRIVIA', color: '#8b5cf6' },
  { id: 3, type: 'normal', label: 'Hustle Hard' },
  { id: 4, type: 'game', label: 'GAME TIME', color: '#f59e0b' },
  { id: 5, type: 'normal', label: 'Side Quest' },
  { id: 6, type: 'prison', label: 'BACK TO PRISON', color: '#dc2626' },
  { id: 7, type: 'normal', label: 'Grind Mode' },
  { id: 8, type: 'trivia', label: 'TRIVIA', color: '#8b5cf6' },
  { id: 9, type: 'normal', label: 'Close Call' },
  { id: 10, type: 'normal', label: 'Stackin\' Paper' },
  { id: 11, type: 'game', label: 'GAME TIME', color: '#f59e0b' },
  { id: 12, type: 'normal', label: 'The Come Up' },
  { id: 13, type: 'trivia', label: 'TRIVIA', color: '#8b5cf6' },
  { id: 14, type: 'normal', label: 'Loyal Friends' },
  { id: 15, type: 'prison', label: 'BACK TO PRISON', color: '#dc2626' },
  { id: 16, type: 'normal', label: 'Betrayal' },
  { id: 17, type: 'game', label: 'GAME TIME', color: '#f59e0b' },
  { id: 18, type: 'normal', label: 'Breaking Free' },
  { id: 19, type: 'trivia', label: 'TRIVIA', color: '#8b5cf6' },
  { id: 20, type: 'normal', label: 'The Deal' },
  { id: 21, type: 'normal', label: 'Close Call' },
  { id: 22, type: 'game', label: 'GAME TIME', color: '#f59e0b' },
  { id: 23, type: 'normal', label: 'Almost There' },
  { id: 24, type: 'trivia', label: 'TRIVIA', color: '#8b5cf6' },
  { id: 25, type: 'normal', label: 'Final Stretch' },
  { id: 26, type: 'prison', label: 'BACK TO PRISON', color: '#dc2626' },
  { id: 27, type: 'normal', label: 'One More Step' },
  { id: 28, type: 'game', label: 'GAME TIME', color: '#f59e0b' },
  { id: 29, type: 'normal', label: 'Victory Lap' },
  { id: 30, type: 'fame', label: 'FAME & FORTUNE', color: '#fbbf24' },
];

const TRIVIA_QUESTIONS: TriviaQuestion[] = [
  {
    id: 1,
    question: 'What is The Mack Rep\'s biological name?',
    options: ['Kiley Kelly', 'Marcus Johnson', 'DeShawn Williams', 'Terrence Brown'],
    correctAnswer: 0,
  },
  {
    id: 2,
    question: 'How many years did The Mack Rep do in prison?',
    options: ['10 years', '15 years', '18 years', '25 years'],
    correctAnswer: 2,
  },
  {
    id: 3,
    question: 'What city and state is The Mack Rep from?',
    options: ['Los Angeles, CA', 'Sacramento, CA', 'Oakland, CA', 'San Francisco, CA'],
    correctAnswer: 1,
  },
  {
    id: 4,
    question: 'What neighborhood did The Mack Rep grow up in?',
    options: ['Compton', 'Southside', 'Meadowview', 'Rancho Cordova'],
    correctAnswer: 2,
  },
  {
    id: 5,
    question: 'What is the name of The Mack Rep\'s company?',
    options: ['Mack Entertainment', 'Go Flicks', 'Street Dreams Inc.', 'Rep Music Group'],
    correctAnswer: 1,
  },
  {
    id: 6,
    question: 'What is a Mack Rep?',
    options: ['A music producer', 'A record label owner', 'A mack that represents other Macks from their area', 'A prison guard'],
    correctAnswer: 2,
  },
  {
    id: 7,
    question: 'What was the name of The Mack Rep\'s first album?',
    options: ['Street Life', 'I\'ll Rather Die Than Be Broke', 'Hustle Hard', 'Born Survivor'],
    correctAnswer: 1,
  },
  {
    id: 8,
    question: 'Finish the sentence: I\'m so fly, I\'m flyer than ______?',
    options: ['Birds', 'Airplanes', 'Southwest', 'The wind'],
    correctAnswer: 2,
  },
  {
    id: 9,
    question: 'What is The Mack Rep\'s truce fund for?',
    options: ['Bail money', 'Studio equipment', 'Small business loans for peace treaties', 'Community center'],
    correctAnswer: 2,
  },
  {
    id: 10,
    question: 'What is the name of The Mack Rep\'s future non-profit?',
    options: ['Peace Makers', 'Injustice League', 'Street Saints', 'Hope Foundation'],
    correctAnswer: 1,
  },
];

const TRUTH_OR_DARE_PROMPTS = [
  // Truth questions
  { type: 'truth' as const, prompt: 'Can you count on both hands how many sex partners you have had or is it more than both hands?' },
  { type: 'truth' as const, prompt: 'Do you have a secret crush on anybody in the room and if yes who?' },
  { type: 'truth' as const, prompt: 'What is your favorite sexual position?' },
  { type: 'truth' as const, prompt: 'What is something that turns you on?' },
  { type: 'truth' as const, prompt: 'What is your most erotic fantasy?' },
  // Dare challenges
  { type: 'dare' as const, prompt: 'I dare you to tongue kiss someone in the room.' },
  { type: 'dare' as const, prompt: 'I dare you to give someone in the room a lap dance.' },
  { type: 'dare' as const, prompt: 'I dare you to flash a private part to everyone in the room.' },
  { type: 'dare' as const, prompt: 'I dare you to sip some of your alcoholic beverage from the naval of someone of the opposite sex.' },
  { type: 'dare' as const, prompt: 'I dare you to record yourself twerking and post it on social media and don\'t forget to hashtag it #igbr2i2d.' },
];

const CHARADES_PROMPTS = [
  'Prison break', 'Counting money', 'Recording a song', 'Driving a luxury car',
  'Being arrested', 'Court trial', 'Running from the police', 'Making it big',
  'Street fight', 'Studio session', 'Signing a deal', 'Flexing',
];

let nextPlayerId = 1;

export const useGameStore = create<GameState>((set, get) => ({
  phase: 'setup',
  currentPlayerIndex: 0,
  players: [],
  boardSpaces: BOARD_SPACES,
  
  diceValue: null,
  isRolling: false,
  canRoll: true,
  
  currentTrivia: null,
  triviaAnswered: false,
  
  showMiniGame: false,
  miniGameType: null,
  charadesTimeLeft: 60,
  
  // Penalty roll
  penaltyRolling: false,
  pendingPenaltySpaces: 0,
  
  // Money confetti
  showMoneyConfetti: false,

  // Pending win
  pendingWin: false,

  // Pending prison
  pendingPrison: false,

  // Charades rolling phase
  charadesRollingPhase: false,
  charadesActorId: null,
  charadesGuesserId: null,
  charadesActorRolled: false,
  charadesGuesserRolled: false,
  charadesWaitingForVideo: false,
  charadesCurrentRollerId: null,
  charadesPrisonPlayerId: null,

  // Video playback
  playingVideo: null as 'the-streets' | 'hustle-hard' | 'side-quest' | 'grind-mode' | 'close-call' | 'stackin-paper' | 'the-come-up' | 'loyal-friends' | 'betrayal' | 'breaking-free' | 'the-deal' | 'close-call-2' | 'almost-there' | 'final-stretch' | 'one-more-step' | 'victory-lap' | 'fame-fortune' | 'back-to-prison' | null,

  // Sidebar state
  sidebarsOpen: false,
  roundTurnsCompleted: 0,
  
  addPlayer: (name: string, color: PlayerColor) => {
    set((state) => ({
      players: [
        ...state.players,
        {
          id: `player-${nextPlayerId++}`,
          name,
          color,
          position: 0,
          isInPrison: true,
          sips: 0,
        },
      ],
    }));
  },
  
  removePlayer: (id: string) => {
    set((state) => ({
      players: state.players.filter((p) => p.id !== id),
    }));
  },
  
  startGame: () => {
    set({ phase: 'playing', canRoll: true });
  },
  
  rollDice: () => {
    const state = get();
    if (!state.canRoll || state.isRolling) return;
    
    set({ isRolling: true, canRoll: false });
    
    // Animate dice for 1 second
    let count = 0;
    const interval = setInterval(() => {
      count++;
      set({ diceValue: Math.floor(Math.random() * 6) + 1 });
      if (count >= 10) {
        clearInterval(interval);
        const finalValue = Math.floor(Math.random() * 6) + 1;
        set({ diceValue: finalValue, isRolling: false });
        
        // Process the roll
        setTimeout(() => {
          get().finishRolling(finalValue);
        }, 500);
      }
    }, 100);
  },
  
  finishRolling: (value: number) => {
    const state = get();
    const currentPlayer = state.players[state.currentPlayerIndex];
    
    if (!currentPlayer) return;
    
    if (currentPlayer.isInPrison) {
      if (value >= 6) {
        // Escape prison! Free them and allow re-roll (don't move yet)
        set((state) => ({
          players: state.players.map((p) =>
            p.id === currentPlayer.id ? { ...p, isInPrison: false } : p
          ),
          canRoll: true,
          phase: 'playing',
        }));
        // Player can now roll again and move by the new dice value
      } else {
        // Still in prison, lose turn
        set({ phase: 'playing' });
        setTimeout(() => {
          get().nextTurn();
        }, 1500);
      }
      return;
    }
    
    // Normal movement
    get().moveCurrentPlayer(value);
  },
  
  moveCurrentPlayer: (spaces: number) => {
    const state = get();
    const currentPlayer = state.players[state.currentPlayerIndex];
    
    if (!currentPlayer) return;
    
    // Exact landing requirement for Fame & Fortune (space 30)
    // If overshooting, stay in place - must roll exact number to win
    const targetPosition = currentPlayer.position + spaces;
    if (targetPosition > 30) {
      // Overshot - stay in place, lose turn
      setTimeout(() => {
        get().nextTurn();
      }, 1000);
      return;
    }
    
    // Add sips equal to spaces moved
    set((state) => ({
      players: state.players.map((p) =>
        p.id === currentPlayer.id
          ? { ...p, position: targetPosition, sips: p.sips + spaces }
          : p
      ),
    }));
    
    // Check the landing space
    const landedSpace = BOARD_SPACES[targetPosition];
    
    // Check for win (must land exactly on 30)
    if (landedSpace.type === 'fame' && targetPosition === 30) {
      set({ playingVideo: 'fame-fortune', pendingWin: true });
      return;
    }
    
    // Handle special spaces
    setTimeout(() => {
      // Check for destination card video triggers
      if (targetPosition === 1) {
        // "The Streets" - play video
        set({ playingVideo: 'the-streets' });
        return;
      } else if (targetPosition === 3) {
        // "Hustle Hard" - play video
        set({ playingVideo: 'hustle-hard' });
        return;
      } else if (targetPosition === 5) {
        // "Side Quest" - play video
        set({ playingVideo: 'side-quest' });
        return;
      } else if (targetPosition === 7) {
        // "Grind Mode" - play video
        set({ playingVideo: 'grind-mode' });
        return;
      } else if (targetPosition === 9) {
        // "Close Call" - play video
        set({ playingVideo: 'close-call' });
        return;
      } else if (targetPosition === 10) {
        // "Stackin' Paper" - play video
        set({ playingVideo: 'stackin-paper' });
        return;
      } else if (targetPosition === 12) {
        // "The Come Up" - play video
        set({ playingVideo: 'the-come-up' });
        return;
      } else if (targetPosition === 14) {
        // "Loyal Friends" - play video
        set({ playingVideo: 'loyal-friends' });
        return;
      } else if (targetPosition === 16) {
        // "Betrayal" - play video
        set({ playingVideo: 'betrayal' });
        return;
      } else if (targetPosition === 18) {
        // "Breaking Free" - play video
        set({ playingVideo: 'breaking-free' });
        return;
      } else if (targetPosition === 20) {
        // "The Deal" - play video
        set({ playingVideo: 'the-deal' });
        return;
      } else if (targetPosition === 21) {
        // "Close Call 2" - play video
        set({ playingVideo: 'close-call-2' });
        return;
      } else if (targetPosition === 23) {
        // "Almost There" - play video
        set({ playingVideo: 'almost-there' });
        return;
      } else if (targetPosition === 25) {
        // "Final Stretch" - play video
        set({ playingVideo: 'final-stretch' });
        return;
      } else if (targetPosition === 27) {
        // "One More Step" - play video
        set({ playingVideo: 'one-more-step' });
        return;
      } else if (targetPosition === 29) {
        // "Victory Lap" - play video
        set({ playingVideo: 'victory-lap' });
        return;
      } else if (landedSpace.type === 'trivia') {
        get().showTriviaQuestion();
      } else if (landedSpace.type === 'game') {
        // Game spots randomly choose between Truth or Dare & Sip or Charades
        const gameType = Math.random() > 0.5 ? 'truth_or_dare' : 'charades';
        get().startMiniGame(gameType);
      } else if (landedSpace.type === 'prison') {
        // "Back to Prison" - play video, then send to prison after video ends
        set({ playingVideo: 'back-to-prison', pendingPrison: true });
        return;
      } else {
        get().nextTurn();
      }
    }, 1000);
  },
  
  movePlayer: (playerId: string, spaces: number) => {
    const state = get();
    const player = state.players.find(p => p.id === playerId);
    if (!player) return;
    
    const newPosition = player.position + spaces;
    
    // If the roll would take them past the 30th space, they stay put
    if (newPosition > 30) {
      return;
    }
    
    set((state) => ({
      players: state.players.map((p) =>
        p.id === playerId
          ? { ...p, position: newPosition, sips: p.sips + spaces }
          : p
      ),
    }));
  },
  
  sendToPrison: (playerId: string) => {
    set((state) => ({
      players: state.players.map((p) =>
        p.id === playerId ? { ...p, position: 0, isInPrison: true } : p
      ),
    }));
  },
  
  freeFromPrison: (playerId: string) => {
    set((state) => ({
      players: state.players.map((p) =>
        p.id === playerId ? { ...p, isInPrison: false } : p
      ),
    }));
  },
  
  nextTurn: () => {
    set((state) => {
      const newCompleted = state.roundTurnsCompleted + 1;
      const shouldClose = newCompleted >= state.players.length && state.sidebarsOpen;
      return {
        currentPlayerIndex: (state.currentPlayerIndex + 1) % state.players.length,
        canRoll: true,
        phase: 'playing',
        roundTurnsCompleted: newCompleted,
        sidebarsOpen: shouldClose ? false : state.sidebarsOpen,
      };
    });
  },
  
  addSips: (playerId: string, amount: number) => {
    set((state) => ({
      players: state.players.map((p) =>
        p.id === playerId ? { ...p, sips: p.sips + amount } : p
      ),
    }));
  },
  
  showTriviaQuestion: () => {
    const question = TRIVIA_QUESTIONS[Math.floor(Math.random() * TRIVIA_QUESTIONS.length)];
    set({ currentTrivia: question, phase: 'trivia', triviaAnswered: false });
  },
  
  answerTrivia: (correct: boolean) => {
    const state = get();
    
    // Charades rolling phase — trivia was triggered from a charades dice roll
    if (state.charadesRollingPhase) {
      set({ currentTrivia: null });
      if (correct) {
        // Correct! Player gets to roll again in charades (dice re-enabled because rolled flag is still false)
        return;
      } else {
        // Wrong! Mark roller as done, move to next roller
        get().charadesMarkRollerDone();
        return;
      }
    }

    // Normal trivia flow
    if (correct) {
      set({ phase: 'playing', currentTrivia: null, canRoll: true });
    } else {
      set({ currentTrivia: null, phase: 'penalty-roll', penaltyRolling: false, pendingPenaltySpaces: 0, canRoll: true, sidebarsOpen: true });
    }
  },
  
  closeTrivia: () => {
    set({ currentTrivia: null, phase: 'playing' });
  },
  
  startMiniGame: (type: 'truth_or_dare' | 'charades') => {
    set({
      showMiniGame: true,
      miniGameType: type,
      charadesTimeLeft: 60,
      phase: 'game',
    });
  },
  
  endMiniGame: (result: boolean | { type: string; actorId?: string; guesserId?: string }) => {
    const state = get();

    set({ showMiniGame: false, miniGameType: null });

    // Handle charades success - start sidebar rolling
    if (typeof result === 'object' && result.type === 'charades-success' && result.actorId && result.guesserId) {
      get().startCharadesRolling(result.actorId, result.guesserId);
      return;
    }

    // Charades rolling phase — game was triggered from a charades dice roll
    if (state.charadesRollingPhase) {
      const success = typeof result === 'boolean' ? result : false;
      if (success) {
        // Success! Player gets to roll again in charades
        return;
      } else {
        // Failed! Mark roller as done, move to next roller
        get().charadesMarkRollerDone();
        return;
      }
    }

    const success = typeof result === 'boolean' ? result : false;

    if (success) {
      // Roll dice and move forward
      set({ canRoll: true, phase: 'playing' });
    } else {
      // Failed: roll dice and move backward by dice value
      set({ phase: 'penalty-roll', penaltyRolling: false, pendingPenaltySpaces: 0, canRoll: true, sidebarsOpen: true });
    }
  },
  
  setCharadesTime: (time: number) => {
    set({ charadesTimeLeft: time });
  },
  
  // Money confetti actions
  triggerMoneyConfetti: () => {
    set({ showMoneyConfetti: true });
  },
  
  hideMoneyConfetti: () => {
    set({ showMoneyConfetti: false });
  },

  setPendingWin: (val: boolean) => {
    set({ pendingWin: val });
  },

  setPendingPrison: (val: boolean) => {
    set({ pendingPrison: val });
  },

  // Charades rolling actions
  startCharadesRolling: (actorId: string, guesserId: string) => {
    set({
      charadesRollingPhase: true,
      charadesActorId: actorId,
      charadesGuesserId: guesserId,
      charadesActorRolled: false,
      charadesGuesserRolled: false,
      charadesCurrentRollerId: actorId,
      charadesWaitingForVideo: false,
      charadesPrisonPlayerId: null,
      phase: 'playing',
      canRoll: false,
      sidebarsOpen: true,
    });
  },

  finishCharadesRolling: (playerId: string, value: number) => {
    const state = get();
    const player = state.players.find(p => p.id === playerId);
    if (!player) return;

    const isActor = playerId === state.charadesActorId;
    const isGuesser = playerId === state.charadesGuesserId;

    // Helper to mark the current roller as done and advance to next roller
    const markDone = () => {
      if (isActor) {
        set({ charadesActorRolled: true, charadesCurrentRollerId: state.charadesGuesserId });
      } else if (isGuesser) {
        set({ charadesGuesserRolled: true, charadesCurrentRollerId: null });
      }
    };

    if (player.isInPrison) {
      if (value === 6) {
        set((s) => ({
          players: s.players.map((p) =>
            p.id === playerId ? { ...p, isInPrison: false } : p
          ),
        }));
      }
      // Whether escaped or not, mark as done
      markDone();
      return;
    }

    const newPosition = player.position + value;

    // Past-30 overshoot rule: stay in place if roll would go past 30
    if (newPosition > 30) {
      markDone();
      return;
    }

    // Move the player
    set((s) => ({
      players: s.players.map((p) =>
        p.id === playerId
          ? { ...p, position: newPosition, sips: p.sips + value }
          : p
      ),
    }));

    // Check if either player landed exactly on Fame & Fortune (space 30) — ends the game immediately
    if (newPosition === 30) {
      set({
        playingVideo: 'fame-fortune',
        pendingWin: true,
        charadesRollingPhase: false,
        charadesActorId: null,
        charadesGuesserId: null,
        charadesActorRolled: false,
        charadesGuesserRolled: false,
        charadesCurrentRollerId: null,
        charadesWaitingForVideo: false,
        charadesPrisonPlayerId: null,
      });
      return;
    }

    const newSpace = BOARD_SPACES[newPosition];

    // Handle destination cards after a brief delay
    setTimeout(() => {
      // Video destination cards
      if (newPosition === 1) {
        set({ playingVideo: 'the-streets', charadesWaitingForVideo: true });
        return;
      } else if (newPosition === 3) {
        set({ playingVideo: 'hustle-hard', charadesWaitingForVideo: true });
        return;
      } else if (newPosition === 5) {
        set({ playingVideo: 'side-quest', charadesWaitingForVideo: true });
        return;
      } else if (newPosition === 7) {
        set({ playingVideo: 'grind-mode', charadesWaitingForVideo: true });
        return;
      } else if (newPosition === 9) {
        set({ playingVideo: 'close-call', charadesWaitingForVideo: true });
        return;
      } else if (newPosition === 10) {
        set({ playingVideo: 'stackin-paper', charadesWaitingForVideo: true });
        return;
      } else if (newPosition === 12) {
        set({ playingVideo: 'the-come-up', charadesWaitingForVideo: true });
        return;
      } else if (newPosition === 14) {
        set({ playingVideo: 'loyal-friends', charadesWaitingForVideo: true });
        return;
      } else if (newPosition === 16) {
        set({ playingVideo: 'betrayal', charadesWaitingForVideo: true });
        return;
      } else if (newPosition === 18) {
        set({ playingVideo: 'breaking-free', charadesWaitingForVideo: true });
        return;
      } else if (newPosition === 20) {
        set({ playingVideo: 'the-deal', charadesWaitingForVideo: true });
        return;
      } else if (newPosition === 21) {
        set({ playingVideo: 'close-call-2', charadesWaitingForVideo: true });
        return;
      } else if (newPosition === 23) {
        set({ playingVideo: 'almost-there', charadesWaitingForVideo: true });
        return;
      } else if (newPosition === 25) {
        set({ playingVideo: 'final-stretch', charadesWaitingForVideo: true });
        return;
      } else if (newPosition === 27) {
        set({ playingVideo: 'one-more-step', charadesWaitingForVideo: true });
        return;
      } else if (newPosition === 29) {
        set({ playingVideo: 'victory-lap', charadesWaitingForVideo: true });
        return;
      } else if (newSpace.type === 'trivia') {
        // Trivia spot - trigger trivia. Don't mark as done yet — reroll on success.
        get().showTriviaQuestion();
        return;
      } else if (newSpace.type === 'game') {
        // Game spot - trigger game. Don't mark as done yet — reroll on success.
        const gameType = Math.random() > 0.5 ? 'truth_or_dare' : 'charades';
        get().startMiniGame(gameType);
        return;
      } else if (newSpace.type === 'prison') {
        // Prison - play video then send to prison after video ends
        set({ playingVideo: 'back-to-prison', charadesWaitingForVideo: true, charadesPrisonPlayerId: playerId });
        return;
      } else {
        // Normal space - mark as done
        markDone();
      }
    }, 1000);
  },

  // Video playback for destination cards
  setPlayingVideo: (video) => {
    set({ playingVideo: video });
  },

  // Continue charades flow after video ends
  charadesContinueAfterVideo: () => {
    const state = get();
    set({ charadesWaitingForVideo: false });

    // If there's a prison pending, send the tracked player to prison
    if (state.charadesPrisonPlayerId) {
      state.sendToPrison(state.charadesPrisonPlayerId);
      set({ charadesPrisonPlayerId: null });
    }

    // Mark the current roller as done and advance
    if (state.charadesCurrentRollerId === state.charadesActorId) {
      set({ charadesActorRolled: true, charadesCurrentRollerId: state.charadesGuesserId });
    } else if (state.charadesCurrentRollerId === state.charadesGuesserId) {
      set({ charadesGuesserRolled: true, charadesCurrentRollerId: null });
    }
  },

  // Mark the current charades roller as done and advance to next roller
  charadesMarkRollerDone: () => {
    const state = get();
    if (state.charadesCurrentRollerId === state.charadesActorId) {
      set({ charadesActorRolled: true, charadesCurrentRollerId: state.charadesGuesserId });
    } else if (state.charadesCurrentRollerId === state.charadesGuesserId) {
      set({ charadesGuesserRolled: true, charadesCurrentRollerId: null });
    }
  },
  
  albumTriggerDice: () => {
    const state = get();
    if (state.phase !== 'playing') return;
    
    // Open sidebars and start a new round tracking
    set({
      sidebarsOpen: true,
      roundTurnsCompleted: 0,
      canRoll: true,
    });
  },
  
  // Penalty roll dice - player rolls to determine how far backward they move
  penaltyRollDice: () => {
    const state = get();
    if (!state.canRoll || state.penaltyRolling) return;
    
    set({ penaltyRolling: true, canRoll: false });
    
    let count = 0;
    const interval = setInterval(() => {
      count++;
      set({ diceValue: Math.floor(Math.random() * 6) + 1 });
      if (count >= 10) {
        clearInterval(interval);
        const finalValue = Math.floor(Math.random() * 6) + 1;
        set({ diceValue: finalValue, penaltyRolling: false });
        
        setTimeout(() => {
          get().finishPenaltyRolling(finalValue);
        }, 500);
      }
    }, 100);
  },
  
  finishPenaltyRolling: (value: number) => {
    const state = get();
    const currentPlayer = state.players[state.currentPlayerIndex];
    
    if (!currentPlayer) return;
    
    const newPosition = Math.max(0, currentPlayer.position - value);
    
    // Move player backward
    set((state) => ({
      players: state.players.map((p) =>
        p.id === currentPlayer.id ? { ...p, position: newPosition } : p
      ),
      phase: 'playing',
    }));
    
    // Check what space they landed on after moving backward
    const landedSpace = BOARD_SPACES[newPosition];
    
    setTimeout(() => {
      const isCharades = state.charadesRollingPhase;

      if (newPosition === 1) {
        set(isCharades ? { playingVideo: 'the-streets', charadesWaitingForVideo: true } : { playingVideo: 'the-streets' });
        return;
      } else if (newPosition === 3) {
        set(isCharades ? { playingVideo: 'hustle-hard', charadesWaitingForVideo: true } : { playingVideo: 'hustle-hard' });
        return;
      } else if (newPosition === 5) {
        set(isCharades ? { playingVideo: 'side-quest', charadesWaitingForVideo: true } : { playingVideo: 'side-quest' });
        return;
      } else if (newPosition === 7) {
        set(isCharades ? { playingVideo: 'grind-mode', charadesWaitingForVideo: true } : { playingVideo: 'grind-mode' });
        return;
      } else if (newPosition === 9) {
        set(isCharades ? { playingVideo: 'close-call', charadesWaitingForVideo: true } : { playingVideo: 'close-call' });
        return;
      } else if (newPosition === 10) {
        set(isCharades ? { playingVideo: 'stackin-paper', charadesWaitingForVideo: true } : { playingVideo: 'stackin-paper' });
        return;
      } else if (newPosition === 12) {
        set(isCharades ? { playingVideo: 'the-come-up', charadesWaitingForVideo: true } : { playingVideo: 'the-come-up' });
        return;
      } else if (newPosition === 14) {
        set(isCharades ? { playingVideo: 'loyal-friends', charadesWaitingForVideo: true } : { playingVideo: 'loyal-friends' });
        return;
      } else if (newPosition === 16) {
        set(isCharades ? { playingVideo: 'betrayal', charadesWaitingForVideo: true } : { playingVideo: 'betrayal' });
        return;
      } else if (newPosition === 18) {
        set(isCharades ? { playingVideo: 'breaking-free', charadesWaitingForVideo: true } : { playingVideo: 'breaking-free' });
        return;
      } else if (newPosition === 20) {
        set(isCharades ? { playingVideo: 'the-deal', charadesWaitingForVideo: true } : { playingVideo: 'the-deal' });
        return;
      } else if (newPosition === 21) {
        set(isCharades ? { playingVideo: 'close-call-2', charadesWaitingForVideo: true } : { playingVideo: 'close-call-2' });
        return;
      } else if (newPosition === 23) {
        set(isCharades ? { playingVideo: 'almost-there', charadesWaitingForVideo: true } : { playingVideo: 'almost-there' });
        return;
      } else if (newPosition === 25) {
        set(isCharades ? { playingVideo: 'final-stretch', charadesWaitingForVideo: true } : { playingVideo: 'final-stretch' });
        return;
      } else if (newPosition === 27) {
        set(isCharades ? { playingVideo: 'one-more-step', charadesWaitingForVideo: true } : { playingVideo: 'one-more-step' });
        return;
      } else if (newPosition === 29) {
        set(isCharades ? { playingVideo: 'victory-lap', charadesWaitingForVideo: true } : { playingVideo: 'victory-lap' });
        return;
      } else if (landedSpace.type === 'trivia') {
        get().showTriviaQuestion();
      } else if (landedSpace.type === 'game') {
        const gameType = Math.random() > 0.5 ? 'truth_or_dare' : 'charades';
        get().startMiniGame(gameType);
      } else if (landedSpace.type === 'prison') {
        set(isCharades
          ? { playingVideo: 'back-to-prison', pendingPrison: true, charadesWaitingForVideo: true, charadesPrisonPlayerId: currentPlayer.id }
          : { playingVideo: 'back-to-prison', pendingPrison: true }
        );
        return;
      } else if (landedSpace.type === 'fame') {
        set({ playingVideo: 'fame-fortune', pendingWin: true });
      } else {
        // Normal space - end turn (or mark charades roller done)
        if (isCharades) {
          get().charadesMarkRollerDone();
        } else {
          get().nextTurn();
        }
      }
    }, 1000);
  },
}));

export const getRandomTruthOrDare = (type?: 'truth' | 'dare') => {
  const filtered = type ? TRUTH_OR_DARE_PROMPTS.filter(p => p.type === type) : TRUTH_OR_DARE_PROMPTS;
  return filtered[Math.floor(Math.random() * filtered.length)];
};

export const getRandomCharade = () => {
  return CHARADES_PROMPTS[Math.floor(Math.random() * CHARADES_PROMPTS.length)];
};
